import "react-toastify/dist/ReactToastify.css";
import "./global.css";
import "./i18n";

import { Authenticator, useAuthenticator } from "@aws-amplify/ui-react";
import { createTheme, GlobalStyles, ThemeProvider } from "@mui/material";
import * as prismic from "@prismicio/client";
import { PrismicProvider, useAllPrismicDocumentsByType } from "@prismicio/react";
import type { Router } from "@remix-run/router";
import * as Sentry from "@sentry/react";
import { Auth } from "aws-amplify";
import AnalysePage from "components/AnalysePage";
import { getEmptyMandatoryCustomFields } from "components/Beneficiar/utils";
import BeneficiaryFormContainer from "components/BeneficiaryFormContainer";
import ChangelogModal from "components/ChangelogModal";
import { CommandPalette } from "components/CommandPalette/CommandPalette";
import ErrorBoundary from "components/ErrorBoundary";
import MaintenanceScreen from "components/MaintenanceScreen";
import ModalBox from "components/Modal";
import StatementFormContainer from "components/PartnerStatementsSettings/StatementFormContainer";
import PressManagement from "components/PressManagement";
import SetupMFAModal from "components/shared/AccountSettings/SetupMFAModal";
import { BPMobileNavBtn } from "components/shared/BPMobileNavBtn";
import { NavigationWrapper } from "components/shared/Navigation";
import { Spinner } from "components/shared/Spinner";
import StyledButton from "components/shared/StyledButton";
import TitleFormContainer from "components/TitleFormContainer";
import URSSAFDeclarationPage from "components/URSSAFDeclarationPage";
import VerificationModal from "components/VerificationModal";
import { EXTERNAL_SIGNATURE_PROVIDER_KEYS, type SignatureProviderKey } from "config/signatureProviders";
import AdminPanel from "containers/AdminPanel";
import AdvanceManagement from "containers/AdvanceManagement";
import AnalyticsPage from "containers/AnalyticsPage/Page";
import ArticleFormContainer from "containers/ArticleFormContainer";
import Beneficiaries from "containers/Beneficiars";
import type { TBeneficiarCustomField } from "containers/Beneficiars/types";
import BPDashboardPage from "containers/BPDashboardPage";
import BPDocumentContainer from "containers/BPDocumentContainer";
import BPSettingsContainer from "containers/BPSettingsContainer";
import BPStatementPage from "containers/BPStatementPage";
import TitlesBeneficiarPortal from "containers/BPTitles";
import ChangelogContainer from "containers/ChangelogContainer";
import ContractValidation from "containers/ContractValidation";
import ContractValidationEdit from "containers/ContractValidationEdit";
import ContractValidationNew from "containers/ContractValidationNew";
import DeclarationPage from "containers/DeclarationPage";
import DownloadStatementArchive from "containers/DownloadStatementArchive";
import Help from "containers/Help";
import LogIn from "containers/LogIn";
import Management from "containers/Management/";
import Notifications from "containers/Notifications";
import OAuthCallbackComponent from "containers/OAuthCallback";
import PaymentPage from "containers/Payment";
import PublicationFormContainer from "containers/PublicationFormContainer";
import Publications from "containers/Publications";
import Settings from "containers/Settings/";
import SignUp from "containers/SignUp";
import TermFormContainer from "containers/TermFormContainer";
import Titles from "containers/Titles";
import Transferees from "containers/Transferees";
import TransfereesFormContainer from "containers/TransfereesFormContainer";
import TransferTermFormContainer from "containers/TransferTermFormContainer";
import TransferTerms from "containers/TransferTerms";
import Works from "containers/Works";
import { PortalType } from "contexts/portal.context";
import { TrackerProvider } from "contexts/tracker";
import { resolveCountryLanguage } from "helpers/countryLanguage";
import { useMixpanelTracker } from "helpers/mixpanel/hooks";
import { getCognitoUserAttribute } from "helpers/utils";
import { reloadAfterCompanySwitch } from "hooks/companySwitchLocation";
import { useAbility } from "hooks/useAbility";
import { useAdapterLocale } from "hooks/useAdapterLocale";
import { useBPCompanySettings } from "hooks/useBPCompanySettings";
import { useCompanySettings } from "hooks/useCompanySettings";
import useHotjar from "hooks/useHotjar";
import { useIsMobile } from "hooks/useIsMobile";
import { useJobs } from "hooks/useJobs";
import { useNotification } from "hooks/useNotification";
import { toggleTheme } from "hooks/useTheme";
import { client } from "prismic";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Provider, shallowEqual } from "react-redux";
import { BrowserRouter, Navigate, RouterProvider, useParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { fetchBeneficiarPersonalInfo } from "store/beneficiaryStore";
import { fetchChannels } from "store/channelStore";
import { fetchContributions } from "store/contributionStore";
import { fetchCustomFields } from "store/customFieldsStore";
import { fetchIntegrations, IntegrationStatus } from "store/integrationsStore";
import { fetchLocales } from "store/localesStore";
import { fetchPeriods } from "store/periodStore";
import {
    applyMaintenanceSignal,
    bypassAppMaintenance,
    fetchCompanyInfo,
    fetchMe,
    fetchMeBP,
    getClosePeriod,
    setCurrentPortal,
    setLocalSettings,
    toggleNavDrawer,
} from "store/settingsStore";
import type { IBeneficiariesCompanies, TPortalType } from "store/types";

import { ValidateEmail } from "./components/BeneficiarInfo/validateEmail";
import Main from "./components/Main";
import { AuthProvider } from "./contexts/auth/AuthContext";
import { sentryCreateBrowserRouter } from "./init-sentry";
import { store, useAppDispatch, useAppSelector } from "./store/store";

const blockMobileUsersCss = {
    position: "sticky" as any,
    top: "0",
    left: "0",
    bottom: "0",
    right: "0",
    width: "100vw",
    height: "100vh",
    padding: "0 16px",
    textAlign: "center" as any,
    color: "white",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontSize: "20px",
    backgroundColor: "#000000AA",
    backdropFilter: "blur(5px)",
    zIndex: "9999",
};

interface LocalChangelog {
    book?: { lastChangelogId: string };
    press?: { lastChangelogId: string };
    beneficiary?: { lastChangelogId: string };
    urssaf?: { lastChangelogId: string };
}

const theme = createTheme({
    components: {
        MuiInputBase: {
            defaultProps: {
                disableInjectingGlobalStyles: true,
            },
        },
    },
});

const App = () => {
    const tracker = useMixpanelTracker();
    if (window.location.pathname === "/beneficiary/validate-email-token") {
        return (
            <BrowserRouter>
                <Provider store={store}>
                    <ThemeProvider theme={theme}>
                        <ValidateEmail />
                    </ThemeProvider>
                </Provider>
            </BrowserRouter>
        );
    }
    return (
        <Authenticator.Provider>
            <AuthProvider>
                <PrismicProvider client={client}>
                    <Provider store={store}>
                        <TrackerProvider tracker={tracker}>
                            <ThemeProvider theme={theme}>
                                <GlobalStyles
                                    styles={{
                                        "@keyframes mui-auto-fill": { from: { display: "block" } },
                                        "@keyframes mui-auto-fill-cancel": { from: { display: "block" } },
                                    }}
                                />
                                <AllRoutes />
                            </ThemeProvider>
                        </TrackerProvider>
                    </Provider>
                </PrismicProvider>
            </AuthProvider>
        </Authenticator.Provider>
    );
};

const AllRoutes = () => {
    const { user, authStatus } = useAuthenticator((context) => [context.user, context.authStatus]);
    const { i18n, t } = useTranslation();
    const ability = useAbility();
    const settings = useAppSelector((state) => state.settings);
    const {
        userData,
        companyInfoStatus,
        userRole,
        accountSettings,
        companySettings,
        showNavDrawer: onOpenNavDrawer,
        showTitleDrawer: onOpenTitleDrawer,
    } = settings;
    // Each re-render of AllRoutes rebuilds the router and re-runs location-based effects in pages:
    // select only the fields used here so unrelated updates (e.g. pending thunks) don't trigger it.
    const { beneficiarPersonalInfo, missingData, isInvited, beneficiarCompagnies } = useAppSelector(
        (state) => ({
            beneficiarPersonalInfo: state.beneficiary.beneficiarPersonalInfo,
            missingData: state.beneficiary.missingData,
            isInvited: state.beneficiary.isInvited,
            beneficiarCompagnies: state.beneficiary.beneficiarCompagnies,
        }),
        shallowEqual,
    );
    const { isMobile } = useIsMobile();
    const { getCompanySettings, isCompany, companyInfos, countryFeatures } = useCompanySettings();
    const { periods, periodsFetchStatus } = useAppSelector(
        (state) => ({ periods: state.period.periods, periodsFetchStatus: state.period.periodsFetchStatus }),
        shallowEqual,
    );

    const closingPeriodModal = useAppSelector((state) => state.closingPeriod.closing);
    const appMaintenance = useAppSelector((state) => state.appMaintenance);
    const [documents, { state: changelogState }] = useAllPrismicDocumentsByType("changelog");
    const dispatch = useAppDispatch();
    const accountType = getCognitoUserAttribute("custom:accountType", user);
    const username = user?.username ? user?.username : user?.getSignInUserSession()?.getIdToken()?.payload["username"];
    const portal: TPortalType | undefined = companySettings?.publisherType || (accountType as "beneficiary");
    const userCompanyId = getCognitoUserAttribute("custom:companyId", user);
    const [newChangelogId, setNewChangelogId] = useState<string | null>(null);
    const [showChangelog, setShowChangelog] = useState(false);
    const [hasChangedCompany, setHasChangedCompany] = useState(false);
    const [isMFAMandatory, setIsMFAMandatory] = useState(false);

    const [showMFA, setShowMFA] = useState(false);
    const [mfaQRCode, setMFAQRCode] = useState("");
    const [mfaVerificationCode, setMFAVerificationCode] = useState("");

    const { customFields, customFieldsStatus } = useAppSelector(
        (state) => ({
            customFields: state.customFields.customFields,
            customFieldsStatus: state.customFields.customFieldsStatus,
        }),
        shallowEqual,
    );
    const [isCompanyPaletteOpen, setIsCompanyPaletteOpen] = useState(false);
    const usesAgreementsOnly = getCompanySettings("uses_agreements_only");
    const usesAgreements = getCompanySettings("uses_agreements");
    const usesAgreementValidation = getCompanySettings("uses_agreement_validation_chain");
    const hasActiveSignatureIntegration = useAppSelector((state) =>
        state.integrations.integrations.some(
            (i) =>
                EXTERNAL_SIGNATURE_PROVIDER_KEYS.includes(i.partner as SignatureProviderKey) &&
                i.status === IntegrationStatus.CONNECTED,
        ),
    );
    const usesAnalytics = getCompanySettings("uses_analytics");
    const usesUrssafDeclarations = getCompanySettings("uses_urssaf_declarations") ?? true;
    const showDeclarationsPage = countryFeatures.urssaf && usesUrssafDeclarations;
    const usesRightsTransfer = getCompanySettings("uses_transfer_term");
    const usesScopedNotifications = getCompanySettings("uses_scoped_notifications");
    const usesCustomRoles = getCompanySettings("uses_custom_roles");
    const { id: routeId } = useParams();
    const companyId = routeId ? +routeId : beneficiarCompagnies[0]?.id ?? undefined;
    const company = beneficiarCompagnies.find((company) => company.id === +companyId);
    const { getBPCompanySettings } = useBPCompanySettings({
        portalType: "beneficiary",
    });
    const usesBPAgreementsOnly = getBPCompanySettings("uses_agreements_only", (company as IBeneficiariesCompanies)?.id);

    const canAccessManagement = ability.can("access", "ManagementPage");
    // Ignore redirection until userData is fetched
    const redirectToAgreements = !userData?.permissions ? false : usesAgreementsOnly || !canAccessManagement;
    const isArteCompany = isCompany["arte"];
    const isCrealoAdmin = accountType === "admin" && (settings.userData?.email ?? "").includes("@crealo.app");
    const isMaintenanceAdmin = accountType === "admin";

    useEffect(() => {
        if (!appMaintenance.unavailable || appMaintenance.bypassed) return;
        const end = appMaintenance.endsAt ? new Date(appMaintenance.endsAt).getTime() : Number.NaN;
        if (Number.isFinite(end) && Date.now() >= end) {
            dispatch(applyMaintenanceSignal(null));
            return;
        }
        const timeout = Number.isFinite(end)
            ? window.setTimeout(() => dispatch(applyMaintenanceSignal(null)), end - Date.now())
            : undefined;
        const poll = window.setInterval(() => {
            dispatch(fetchCompanyInfo());
        }, 60_000);
        return () => {
            if (timeout !== undefined) window.clearTimeout(timeout);
            window.clearInterval(poll);
        };
    }, [appMaintenance.unavailable, appMaintenance.bypassed, appMaintenance.endsAt, dispatch]);

    // Hotjar Hooks
    useHotjar(username, accountType, portal, userCompanyId);

    const emptyMandatoryCustomFields = useMemo(() => {
        if (portal !== "beneficiary") return;
        return getEmptyMandatoryCustomFields(
            customFields,
            beneficiarPersonalInfo?.customFields as TBeneficiarCustomField[],
        );
    }, [beneficiarPersonalInfo, customFields]);

    const hasEmptyMandatoryInfo =
        portal === "beneficiary" && isInvited && (!!emptyMandatoryCustomFields?.length || !!missingData.length);

    useEffect(() => {
        if (isMobile) {
            const location = window.location.pathname;
            if (onOpenNavDrawer) {
                toggleTheme("light-grey");
            } else if (location === "/dashboard" && !onOpenNavDrawer) {
                toggleTheme("pastel-purple");
            } else if (
                location.includes("titles") ||
                location.includes("statements") ||
                location.includes("documents") ||
                location.includes("settings")
            ) {
                onOpenTitleDrawer ? toggleTheme("light-grey") : toggleTheme("default");
            }
        }
    }, [isMobile, onOpenNavDrawer, onOpenTitleDrawer]);

    const commandPalettePortals: TPortalType[] = ["book", "press", "audiovisual"];

    useEffect(() => {
        if (!isCrealoAdmin || !portal || !commandPalettePortals.includes(portal)) return;
        const handler = (event: KeyboardEvent) => {
            const isMac = navigator.platform.toUpperCase().includes("MAC");
            const isCmdOrCtrl = isMac ? event.metaKey : event.ctrlKey;
            if (!isCmdOrCtrl) return;
            const key = event.key.toLowerCase();
            if (key !== "p" && key !== "k") return;
            event.preventDefault();
            setIsCompanyPaletteOpen((prev) => !prev);
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [isCrealoAdmin, portal]);

    const lastImportantChangelog = useMemo(
        () =>
            documents &&
            documents.length > 0 &&
            documents
                .filter((document) => {
                    const portalTypes = document.data.portaltypes.map(
                        (portalType: { portaltype: string }) => portalType.portaltype,
                    );
                    return portalTypes.includes(portal) && document.data.important.length > 0;
                })
                .sort(
                    (a, b) =>
                        prismic.asDate(b.last_publication_date).getTime() -
                        prismic.asDate(a.last_publication_date).getTime(),
                )[0],
        [documents, portal],
    );

    const checkNewChangelog = () => {
        if (lastImportantChangelog == undefined || !lastImportantChangelog) return null;
        const localeNewChangelog = localStorage.getItem("changelog");

        if (localeNewChangelog == null) {
            const changelogLocale: LocalChangelog = {
                [portal]: { lastChangelogId: lastImportantChangelog.id },
            };
            localStorage.setItem("changelog", JSON.stringify(changelogLocale));
            return lastImportantChangelog.id;
        }

        const localeNewChangelogParsed: LocalChangelog = JSON.parse(localeNewChangelog);

        if (localeNewChangelogParsed[portal]?.lastChangelogId == lastImportantChangelog.id) return null;
        else if (lastImportantChangelog.id != localeNewChangelogParsed[portal]?.lastChangelogId) {
            setShowChangelog(true);
            return lastImportantChangelog.id;
        }

        return null;
    };

    const handleCloseChangelog = () => {
        const localeNewChangelog = localStorage.getItem("changelog");
        if (localeNewChangelog == null) return;
        if (lastImportantChangelog == undefined || !lastImportantChangelog) return;
        setShowChangelog(false);
        const localeNewChangelogParsed: LocalChangelog = JSON.parse(localeNewChangelog);
        const changelogLocale: LocalChangelog = {
            ...localeNewChangelogParsed,
            [portal]: { lastChangelogId: lastImportantChangelog.id },
        };
        localStorage.setItem("changelog", JSON.stringify(changelogLocale));
        setNewChangelogId(lastImportantChangelog.id);
    };

    const onCompanyChange = (e: StorageEvent) => {
        if (e.key !== "companyId") return;
        setHasChangedCompany(true);
        // window.location.reload()
    };

    useEffect(() => {
        if (changelogState != "loaded") return;
        if (lastImportantChangelog == undefined || !lastImportantChangelog) return;
        setNewChangelogId(checkNewChangelog());
    }, [changelogState, lastImportantChangelog]);

    useEffect(() => {
        if (accountType && customFieldsStatus === "idle") {
            dispatch(fetchCustomFields());
        }
        if (accountType && accountType !== "beneficiary") {
            if (!periods.length && periodsFetchStatus === "idle") {
                dispatch(fetchPeriods());
            }
            if (!companySettings) {
                dispatch(getClosePeriod());
                if (companyInfoStatus === "idle") {
                    dispatch(fetchCompanyInfo());
                }
                dispatch(fetchMe());

                // Having it here might save us from fetching again in various components:
                dispatch(fetchChannels());
                dispatch(fetchContributions());
                dispatch(fetchLocales());
            } else {
                if (portal === "book" || portal === "press" || portal === "audiovisual") {
                    dispatch(fetchIntegrations());
                }
            }
        }
        if (accountType && accountType == "beneficiary") {
            dispatch(fetchMeBP()).then((res) => {
                const companyId = res.payload?.companies?.[0]?.companyId ?? undefined;
                if (companyId) {
                    dispatch(fetchBeneficiarPersonalInfo({ companyId: companyId as number }));
                }
            });
        }
        dispatch(setCurrentPortal(portal));

        setIsMFAMandatory(
            companySettings?.settings.some((s) => s.name === "enforce_mfa" && +s.content === 1) ||
                accountType === "admin" ||
                false,
        );
    }, [companySettings, accountType, dispatch]);

    const adapterLocale = useAdapterLocale();

    useEffect(() => {
        let langCode = "";
        console.log("accountSettings", accountSettings.language);
        if (accountSettings.language) {
            langCode = accountSettings.language;
            i18n.changeLanguage(langCode);
        } else if (adapterLocale) {
            // On first ever login (or if not local settings), take navigator's language.
            langCode = adapterLocale.code;

            // If their language is formatted as en-US, only take beginning of code:
            if (adapterLocale.code.includes("-")) {
                langCode = adapterLocale.code.split("-")[0];
            }

            // If their navigator language is neither english nor french, default to english:
            if (!i18n.languages.includes(langCode)) {
                langCode = "en";
            }
            i18n.changeLanguage(langCode);
        }
        dispatch(
            setLocalSettings({
                ...settings,
                accountSettings: { language: langCode, languageSetByUser: accountSettings.languageSetByUser },
            }),
        );
        Sentry.setTag("locale", langCode);
    }, [i18n, adapterLocale]);

    // Company info is fetched from several places (useCompanySettings, navigation…), so the country
    // bundle must follow the loaded company rather than a specific fetch resolution.
    const companyCountryCode = companyInfos?.countryCode;
    useEffect(() => {
        if (!companyCountryCode || !accountSettings.language) return;
        const countryLanguage = resolveCountryLanguage(companyCountryCode, accountSettings.language);
        if (i18n.language !== countryLanguage) i18n.changeLanguage(countryLanguage);
    }, [companyCountryCode, accountSettings.language, i18n]);

    useEffect(() => {
        window.addEventListener("storage", onCompanyChange);

        return () => {
            window.removeEventListener("storage", onCompanyChange);
        };
    }, []);

    // Hook notification => pass user
    useNotification({ user: user });
    useJobs(accountType);

    const handleActivateMFA = async () => {
        if (!showMFA) {
            /**
             * Show modal
             * Request qr code to scan
             */
            setShowMFA(true);

            const secretCode = await Auth.setupTOTP(user);
            const str =
                "otpauth://totp/Crealo:" +
                user.attributes?.["email"] +
                "?secret=" +
                secretCode +
                "&issuer=" +
                encodeURIComponent("https://manage.crealo.app/") +
                "&image=" +
                encodeURIComponent(
                    "https://assets-global.website-files.com/5f96c1e75e94f64c995cffee/611fa59c348a796a2bdc1ff4_iconWebflowIpho.png",
                );
            setMFAQRCode(str);
        } else {
            if (!mfaVerificationCode) return;
            /**
             * Second call, modal is shown
             * code might be written and must be verified
             */
            await Auth.verifyTotpToken(user, mfaVerificationCode);

            await Auth.setPreferredMFA(user, "TOTP");

            /**
             * Show success toast
             */
            setShowMFA(false);
        }
    };

    useEffect(() => {
        Sentry.setUser({
            email: userData?.email,
            id: userData?.id,
            username: `${userData?.firstname} ${userData?.lastname}`,
        });
    }, [userData?.email, userData?.firstname, userData?.id, userData?.lastname]);
    useEffect(() => {
        Sentry.setTag("companyId", companySettings?.id);
        Sentry.setTag("portal", portal);
    }, [companySettings?.id, portal]);

    const renderOnEveryRoute = (router: Router) => (
        <>
            <RouterProvider router={router} />
            <ToastContainer position={isMobile ? "top-left" : "bottom-left"} theme="light" autoClose={2000} />
            {userData && <VerificationModal userData={userData} />}
            {accountType !== "beneficiary" &&
                isMFAMandatory &&
                (user as unknown as any).preferredMFA === "NOMFA" &&
                (handleActivateMFA() as any) && (
                    <ModalBox mainTitle={t("Settings.AccountMFA")} open onClose={() => {}} showCloseButton={false}>
                        <SetupMFAModal
                            mfaQRCode={mfaQRCode}
                            mfaVerificationCode={mfaVerificationCode}
                            onMFAVerificationCode={setMFAVerificationCode}
                            handleSubmit={handleActivateMFA}
                        />
                    </ModalBox>
                )}
            <CommandPalette
                open={isCompanyPaletteOpen && isCrealoAdmin}
                onClose={() => setIsCompanyPaletteOpen(false)}
                navigateTo={(path, state) => router.navigate(path, { state })}
                portalType={portal}
            />
        </>
    );

    const renderOnEveryPublisherPortal = (router: Router) => (
        <>
            {renderOnEveryRoute(router)}
            <ModalBox
                onClose={() => {}}
                open={hasChangedCompany}
                mainTitle={t("NavigationBar.CompanyChanged")}
                showCloseButton={false}
            >
                <div style={{ textAlign: "center" }}>
                    <span style={{ display: "block", marginBottom: "16px" }}>
                        {t("NavigationBar.CompanyChangeTab")}
                        <br />
                        {t("NavigationBar.PleaseRefresh")}
                    </span>
                    <StyledButton buttonType="primary" fullWidth onClick={() => reloadAfterCompanySwitch()}>
                        {t("NavigationBar.Refresh")}
                    </StyledButton>
                </div>
            </ModalBox>
        </>
    );

    if (appMaintenance.unavailable && !(isMaintenanceAdmin && appMaintenance.bypassed)) {
        return (
            <MaintenanceScreen
                startsAt={appMaintenance.startsAt}
                endsAt={appMaintenance.endsAt}
                title={appMaintenance.title}
                description={appMaintenance.description}
                onBypass={isMaintenanceAdmin ? () => dispatch(bypassAppMaintenance()) : undefined}
            />
        );
    }

    if (isMobile && portal && portal !== "beneficiary") {
        return <div style={blockMobileUsersCss}>{t("NavigationBar.MobileNotReady")}</div>;
    }

    if (user === undefined) {
        // `user` is already undefined while Amplify is still checking the session.
        // Mounting the form during that window catches the signOut of an expired
        // refresh token and leaves the submit button pending with a false error.
        if (authStatus !== "unauthenticated") return null;

        const router = sentryCreateBrowserRouter([
            {
                path: "*",
                element: <LogIn />,
                errorElement: (
                    <ErrorBoundary translate={t}>
                        <LogIn />
                    </ErrorBoundary>
                ),
            },
        ]);
        return (
            <>
                <RouterProvider router={router} />
                <ToastContainer position="bottom-left" theme="light" autoClose={2000} />
            </>
        );
    }

    if (portal === "urssaf") {
        const router = sentryCreateBrowserRouter([
            { path: "*", element: <Navigate to="/" replace /> },
            {
                path: "/",
                element: <NavigationWrapper />,
                errorElement: (
                    <ErrorBoundary translate={t}>
                        <NavigationWrapper />
                    </ErrorBoundary>
                ),
                children: [
                    {
                        path: "/",
                        element: <Navigate to="/declarer" replace />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Navigate to="/declarer" replace />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/declarer",
                        element: <URSSAFDeclarationPage />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <URSSAFDeclarationPage />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/analyse",
                        element: <AnalysePage />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <AnalysePage />
                            </ErrorBoundary>
                        ),
                    },
                    // {path: "/resource", element: <ResourcePage />, errorElement: <ErrorBoundary translate={t}><ResourcePage /></ErrorBoundary>},
                    {
                        path: "/settings",
                        element: <Settings />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/company",
                        element: <Settings page="company" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="company" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/account",
                        element: <Settings page="account" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="account" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/members",
                        element: <Settings page="members" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="members" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/accounting",
                        element: <Settings page="accounting" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="accounting" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        ...(accountType === "admin" && {
                            path: "/admin-panel/:tabName",
                            element: <AdminPanel />,
                            errorElement: (
                                <ErrorBoundary translate={t}>
                                    <AdminPanel />
                                </ErrorBoundary>
                            ),
                        }),
                    },
                    {
                        ...(accountType === "admin" && {
                            path: "/settings/api",
                            element: <Settings page="api" />,
                            errorElement: (
                                <ErrorBoundary translate={t}>
                                    <Settings page="api" />
                                </ErrorBoundary>
                            ),
                        }),
                    },
                    {
                        ...(ability.can("access", "PermissionSettings") &&
                            usesCustomRoles && {
                                path: "/settings/permissions",
                                element: <Settings page="permissions" />,
                                errorElement: (
                                    <ErrorBoundary translate={t}>
                                        <Settings page="permissions" />
                                    </ErrorBoundary>
                                ),
                            }),
                    },
                ],
            },
        ]);

        return (
            <>
                {/* <ChangelogModal isOpen={showChangelog} changelogMessage={lastImportantChangelog} onClose={() => handleCloseChangelog()} /> */}
                <PortalType.Provider value={portal}>{renderOnEveryPublisherPortal(router)}</PortalType.Provider>
            </>
        );
    }

    if (["press", "audiovisual"].includes(portal)) {
        const router = sentryCreateBrowserRouter([
            { path: "*", element: <Navigate to="/" replace /> },
            {
                path: "/",
                element: <NavigationWrapper />,
                errorElement: (
                    <ErrorBoundary translate={t}>
                        <NavigationWrapper />
                    </ErrorBoundary>
                ),
                children: [
                    {
                        path: "/",
                        element: <Navigate to={redirectToAgreements ? "/agreements" : "/management"} replace />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Navigate to={redirectToAgreements ? "/agreements" : "/management"} replace />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        ...((canAccessManagement || isArteCompany) && {
                            path: "/management",
                            element: <PressManagement />,
                            errorElement: (
                                <ErrorBoundary translate={t}>
                                    <PressManagement />
                                </ErrorBoundary>
                            ),
                        }),
                    },
                    {
                        ...((ability.can("access", "PublicationsPage") || isArteCompany) && {
                            path: "/publications",
                            element: <Publications />,
                            errorElement: (
                                <ErrorBoundary translate={t}>
                                    <Publications />
                                </ErrorBoundary>
                            ),
                        }),
                    },
                    {
                        ...((ability.can("access", "PublicationsPage") || isArteCompany) && {
                            path: "/publications/:id",
                            element: <Publications />,
                            errorElement: (
                                <ErrorBoundary translate={t}>
                                    <Publications />
                                </ErrorBoundary>
                            ),
                        }),
                    },
                    {
                        ...(ability.can("access", "BeneficiarsPage") && {
                            path: "/beneficiars/:id?",
                            element: <Beneficiaries />,
                            errorElement: (
                                <ErrorBoundary translate={t}>
                                    <Beneficiaries />
                                </ErrorBoundary>
                            ),
                        }),
                    },
                    {
                        ...(ability.can("access", "PaymentPage") && {
                            path: "/payment",
                            element: <PaymentPage />,
                            errorElement: (
                                <ErrorBoundary translate={t}>
                                    <PaymentPage />
                                </ErrorBoundary>
                            ),
                        }),
                    },
                    {
                        ...(showDeclarationsPage &&
                            ability.can("access", "DeclarationsPage") && {
                                path: "/declarations",
                                element: <DeclarationPage />,
                                errorElement: (
                                    <ErrorBoundary translate={t}>
                                        <DeclarationPage />
                                    </ErrorBoundary>
                                ),
                            }),
                    },
                    {
                        ...((usesAgreements || usesAgreementValidation || hasActiveSignatureIntegration) &&
                            ability.can("create", "Agreement") && {
                                path: "/agreements/new",
                                element: <ContractValidationNew />,
                                errorElement: (
                                    <ErrorBoundary translate={t}>
                                        <ContractValidationNew />
                                    </ErrorBoundary>
                                ),
                            }),
                    },
                    {
                        ...((usesAgreements || usesAgreementValidation || hasActiveSignatureIntegration) && {
                            path: "/agreements",
                            element: <ContractValidation />,
                            errorElement: (
                                <ErrorBoundary translate={t}>
                                    <ContractValidation />
                                </ErrorBoundary>
                            ),
                        }),
                    },
                    {
                        ...((usesAgreements || usesAgreementValidation || hasActiveSignatureIntegration) && {
                            path: "/agreements/:id",
                            element: <ContractValidationEdit />,
                            errorElement: (
                                <ErrorBoundary translate={t}>
                                    <ContractValidationEdit />
                                </ErrorBoundary>
                            ),
                        }),
                    },
                    {
                        path: "/notifications",
                        element: <Notifications />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Notifications />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings",
                        element: <Settings page={undefined} />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page={undefined} />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/account",
                        element: <Settings page="account" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="account" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/company",
                        element: <Settings page="company" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="company" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/brand",
                        element: <Settings page="brand" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="brand" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/channels",
                        element: <Settings page="channels" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="channels" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/members",
                        element: <Settings page="members" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="members" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        ...(usesScopedNotifications && {
                            path: "/settings/notifications",
                            element: <Settings page="notifications" />,
                            errorElement: (
                                <ErrorBoundary translate={t}>
                                    <Settings page="notifications" />
                                </ErrorBoundary>
                            ),
                        }),
                    },
                    {
                        path: "/settings/statements",
                        element: <Settings page="statements" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="statements" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/email",
                        element: <Settings page="email" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="email" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/contracts",
                        element: <Settings page="contracts" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="contracts" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/accounting",
                        element: <Settings page="accounting" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="accounting" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/history",
                        element: <Settings page="history" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="history" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/analytical",
                        element: <Settings page="analytical" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="analytical" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/export",
                        element: <Settings page="export" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="export" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        // Shared element: the tab URL changes without remounting the page.
                        element: <Settings page="metadata" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="metadata" />
                            </ErrorBoundary>
                        ),
                        children: [
                            { path: "/settings/metadata" },
                            { path: "/settings/custom-title" },
                            { path: "/settings/custom-beneficiary" },
                            { path: "/settings/custom-term" },
                        ],
                    },
                    {
                        path: "/settings/beneficiary-documents",
                        element: <Settings page="beneficiaryDocuments" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="beneficiaryDocuments" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/changelog",
                        element: <ChangelogContainer portal={portal} />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <ChangelogContainer portal={portal} />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/statement/download",
                        element: <DownloadStatementArchive />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <DownloadStatementArchive />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        ...(accountType === "admin" && {
                            path: "/admin-panel/:tabName",
                            element: <AdminPanel />,
                            errorElement: (
                                <ErrorBoundary translate={t}>
                                    <AdminPanel />
                                </ErrorBoundary>
                            ),
                        }),
                    },
                    {
                        path: "/settings/api",
                        element: <Settings page="api" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="api" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        ...(ability.can("access", "PermissionSettings") &&
                            usesCustomRoles && {
                                path: "/settings/permissions",
                                element: <Settings page="permissions" />,
                                errorElement: (
                                    <ErrorBoundary translate={t}>
                                        <Settings page="permissions" />
                                    </ErrorBoundary>
                                ),
                            }),
                    },
                    {
                        path: "/settings/services",
                        element: <Settings page="services" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="services" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/integrations",
                        element: <Settings page="integrations" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="integrations" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/docoon",
                        element: <Settings page="docoon" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="docoon" />
                            </ErrorBoundary>
                        ),
                    },
                ],
            },
            {
                path: "/publications/new",
                element: <PublicationFormContainer mode="new" />,
                errorElement: (
                    <ErrorBoundary translate={t}>
                        <PublicationFormContainer mode="new" />
                    </ErrorBoundary>
                ),
            },
            {
                path: "/articles/new",
                element: <ArticleFormContainer mode="new" />,
                errorElement: (
                    <ErrorBoundary translate={t}>
                        <ArticleFormContainer mode="new" />
                    </ErrorBoundary>
                ),
            },
            {
                path: "/beneficiaries/new",
                element: <BeneficiaryFormContainer mode="new" />,
                errorElement: (
                    <ErrorBoundary translate={t}>
                        <BeneficiaryFormContainer mode="new" />
                    </ErrorBoundary>
                ),
            },
        ]);

        return (
            <>
                <ChangelogModal
                    isOpen={showChangelog}
                    changelogMessage={lastImportantChangelog}
                    onClose={() => handleCloseChangelog()}
                />
                <PortalType.Provider value={portal}>{renderOnEveryPublisherPortal(router)}</PortalType.Provider>
            </>
        );
    }

    if (accountType === "beneficiary") {
        const router = sentryCreateBrowserRouter([
            { path: "*", element: <Navigate to="/" replace /> },
            {
                path: "/",
                element: <NavigationWrapper />,
                errorElement: (
                    <ErrorBoundary translate={t}>
                        <NavigationWrapper />
                    </ErrorBoundary>
                ),
                children: [
                    {
                        path: "/",
                        element: <Navigate to={hasEmptyMandatoryInfo ? "/settings" : "/dashboard"} replace />,
                    },
                    {
                        ...(!hasEmptyMandatoryInfo && {
                            path: "/dashboard",
                            element: <BPDashboardPage />,
                            errorElement: (
                                <ErrorBoundary translate={t}>
                                    <BPDashboardPage />
                                </ErrorBoundary>
                            ),
                        }),
                    },
                    {
                        ...(!hasEmptyMandatoryInfo && {
                            path: "/titles",
                            element: <TitlesBeneficiarPortal />,
                            errorElement: (
                                <ErrorBoundary translate={t}>
                                    <TitlesBeneficiarPortal />
                                </ErrorBoundary>
                            ),
                        }),
                    },
                    {
                        ...(!hasEmptyMandatoryInfo && {
                            path: "/titles/:id",
                            element: <TitlesBeneficiarPortal />,
                            errorElement: (
                                <ErrorBoundary translate={t}>
                                    <TitlesBeneficiarPortal />
                                </ErrorBoundary>
                            ),
                        }),
                    },
                    {
                        ...(!hasEmptyMandatoryInfo &&
                            !usesBPAgreementsOnly && {
                                path: "/statements",
                                element: <BPStatementPage />,
                                errorElement: (
                                    <ErrorBoundary translate={t}>
                                        <BPStatementPage />
                                    </ErrorBoundary>
                                ),
                            }),
                    },
                    {
                        path: "/settings",
                        element: <BPSettingsContainer page="profile" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <BPSettingsContainer page="profile" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/profile",
                        element: <BPSettingsContainer page="profile" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <BPSettingsContainer page="profile" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/account/:id",
                        element: <BPSettingsContainer page="account" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <BPSettingsContainer page="account" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        ...(!usesBPAgreementsOnly && {
                            path: "/settings/tax/:id",
                            element: <BPSettingsContainer page="tax" />,
                            errorElement: (
                                <ErrorBoundary translate={t}>
                                    <BPSettingsContainer page="tax" />
                                </ErrorBoundary>
                            ),
                        }),
                    },
                    {
                        ...(!usesBPAgreementsOnly && {
                            path: "/settings/payment/:id",
                            element: <BPSettingsContainer page="payment" />,
                            errorElement: (
                                <ErrorBoundary translate={t}>
                                    <BPSettingsContainer page="payment" />
                                </ErrorBoundary>
                            ),
                        }),
                    },
                    {
                        ...(!hasEmptyMandatoryInfo && {
                            path: "/documents",
                            element: <BPDocumentContainer />,
                            errorElement: (
                                <ErrorBoundary translate={t}>
                                    <BPDocumentContainer />
                                </ErrorBoundary>
                            ),
                        }),
                    },
                    {
                        path: "/notifications",
                        element: <Notifications />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Notifications />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/changelog",
                        element: <ChangelogContainer portal="beneficiary" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <ChangelogContainer portal="beneficiary" />
                            </ErrorBoundary>
                        ),
                    },
                ],
            },
        ]);

        return (
            <>
                <ChangelogModal
                    isOpen={showChangelog}
                    changelogMessage={lastImportantChangelog}
                    onClose={() => handleCloseChangelog()}
                />
                {isMobile && (
                    <>
                        <BPMobileNavBtn
                            onOpenNavDrawer={onOpenNavDrawer}
                            openNavDrawer={() => dispatch(toggleNavDrawer(true))}
                            closeNavDrawer={() => dispatch(toggleNavDrawer(false))}
                        />
                    </>
                )}
                <PortalType.Provider value={portal}>{renderOnEveryRoute(router)}</PortalType.Provider>
            </>
        );
    }

    if (portal === "book") {
        const router = sentryCreateBrowserRouter([
            { path: "*", element: <Navigate to={redirectToAgreements ? "/agreements" : "/management"} replace /> },
            {
                path: "/main",
                element: <Main />,
                errorElement: (
                    <ErrorBoundary translate={t}>
                        <Main />
                    </ErrorBoundary>
                ),
            },
            {
                path: "/signup",
                element: <SignUp />,
                errorElement: (
                    <ErrorBoundary translate={t}>
                        <SignUp />
                    </ErrorBoundary>
                ),
            },
            {
                path: "/",
                element: <NavigationWrapper />,
                errorElement: (
                    <ErrorBoundary translate={t}>
                        <NavigationWrapper />
                    </ErrorBoundary>
                ),
                children: [
                    {
                        path: "/",
                        element: <Navigate to={redirectToAgreements ? "/agreements" : "/management"} replace />,
                    },
                    // Analytics page temporarily disabled (maintenance)
                    {
                        ...(usesAnalytics && {
                            path: "/dashboard",
                            element: <AnalyticsPage />,
                            errorElement: (
                                <ErrorBoundary translate={t}>
                                    <AnalyticsPage />
                                </ErrorBoundary>
                            ),
                        }),
                    },
                    {
                        ...(!redirectToAgreements && {
                            path: "/management",
                            element: <Management />,
                            errorElement: (
                                <ErrorBoundary translate={t}>
                                    <Management />
                                </ErrorBoundary>
                            ),
                        }),
                    },
                    {
                        ...(!redirectToAgreements && {
                            path: "/management/:authorId/:titleId",
                            element: <Management />,
                            errorElement: (
                                <ErrorBoundary translate={t}>
                                    <Management />
                                </ErrorBoundary>
                            ),
                        }),
                    },
                    {
                        ...(!redirectToAgreements && {
                            path: "/advance-payment",
                            element: <AdvanceManagement />,
                            errorElement: (
                                <ErrorBoundary translate={t}>
                                    <AdvanceManagement />
                                </ErrorBoundary>
                            ),
                        }),
                    },
                    {
                        ...(!redirectToAgreements &&
                            usesRightsTransfer && {
                                path: "/transfer-terms",
                                element: <TransferTerms />,
                                errorElement: (
                                    <ErrorBoundary translate={t}>
                                        <TransferTerms />
                                    </ErrorBoundary>
                                ),
                            }),
                    },
                    {
                        path: "/works/*",
                        element: <Works />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Works />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/titles",
                        element: <Titles />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Titles />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/titles/:id",
                        element: <Titles />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Titles />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/beneficiars/:id?",
                        element: <Beneficiaries />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Beneficiaries />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings",
                        element: <Settings />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/account",
                        element: <Settings page="account" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="account" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/email",
                        element: <Settings page="email" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="email" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/members",
                        element: <Settings page="members" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="members" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/notifications",
                        element: <Settings page="notifications" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="notifications" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/statements",
                        element: <Settings page="statements" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="statements" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/company",
                        element: <Settings page="company" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="company" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/accounting",
                        element: <Settings page="accounting" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="accounting" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/advance-provision",
                        element: <Settings page="advance-provision" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="advance-provision" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/advance-statuses",
                        element: <Settings page="advance-statuses" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="advance-statuses" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/history",
                        element: <Settings page="history" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="history" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/channels",
                        element: <Settings page="channels" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="channels" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/channels/:id",
                        element: <Settings page="channels" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="channels" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/format-types",
                        element: <Settings page="formatTypes" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="formatTypes" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/sales",
                        element: <Settings page="sales" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="sales" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/calculation-base-labels",
                        element: <Navigate to="/settings/sales" replace />,
                    },
                    {
                        path: "/settings/export",
                        element: <Settings page="export" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="export" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/analytical",
                        element: <Settings page="analytical" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="analytical" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/brand",
                        element: <Settings page="brand" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="brand" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/contracts",
                        element: <Settings page="contracts" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="contracts" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/term-templates",
                        element: <Settings page="terms" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="terms" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/currency",
                        element: <Settings page="currency" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="currency" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/edi-interforum",
                        element: <Settings page="edi-interforum" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="edi-interforum" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/integrations",
                        element: <Settings page="integrations" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="integrations" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/docoon",
                        element: <Settings page="docoon" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="docoon" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/partner-statements",
                        element: <Settings page="partner-statements" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="partner-statements" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        // Shared element: the tab URL changes without remounting the page.
                        element: <Settings page="metadata" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="metadata" />
                            </ErrorBoundary>
                        ),
                        children: [
                            { path: "/settings/metadata" },
                            { path: "/settings/custom-title" },
                            { path: "/settings/custom-beneficiary" },
                            { path: "/settings/custom-term" },
                        ],
                    },
                    {
                        path: "/settings/beneficiary-documents",
                        element: <Settings page="beneficiaryDocuments" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="beneficiaryDocuments" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/settings/invoice-percentages",
                        element: <Settings page="invoicePercentages" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="invoicePercentages" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/help",
                        element: <Help />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Help />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        ...(!redirectToAgreements && {
                            path: "/payment",
                            element: <PaymentPage />,
                            errorElement: (
                                <ErrorBoundary translate={t}>
                                    <PaymentPage />
                                </ErrorBoundary>
                            ),
                        }),
                    },
                    {
                        ...(!redirectToAgreements &&
                            showDeclarationsPage && {
                                path: "/declarations",
                                element: <DeclarationPage />,
                                errorElement: (
                                    <ErrorBoundary translate={t}>
                                        <DeclarationPage />
                                    </ErrorBoundary>
                                ),
                            }),
                    },
                    {
                        path: "/notifications",
                        element: <Notifications />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Notifications />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/changelog",
                        element: <ChangelogContainer portal="book" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <ChangelogContainer portal="book" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        ...((usesAgreements || usesAgreementValidation || hasActiveSignatureIntegration) && {
                            path: "/agreements/new",
                            element: <ContractValidationNew />,
                            errorElement: (
                                <ErrorBoundary translate={t}>
                                    <ContractValidationNew />
                                </ErrorBoundary>
                            ),
                        }),
                    },
                    {
                        ...((usesAgreements || usesAgreementValidation || hasActiveSignatureIntegration) && {
                            path: "/agreements",
                            element: <ContractValidation />,
                            errorElement: (
                                <ErrorBoundary translate={t}>
                                    <ContractValidation />
                                </ErrorBoundary>
                            ),
                        }),
                    },
                    {
                        ...((usesAgreements || usesAgreementValidation || hasActiveSignatureIntegration) && {
                            path: "/agreements/:id",
                            element: <ContractValidationEdit />,
                            errorElement: (
                                <ErrorBoundary translate={t}>
                                    <ContractValidationEdit />
                                </ErrorBoundary>
                            ),
                        }),
                    },
                    {
                        path: "/settings/api",
                        element: <Settings page="api" />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Settings page="api" />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        ...(ability.can("access", "PermissionSettings") &&
                            usesCustomRoles && {
                                path: "/settings/permissions",
                                element: <Settings page="permissions" />,
                                errorElement: (
                                    <ErrorBoundary translate={t}>
                                        <Settings page="permissions" />
                                    </ErrorBoundary>
                                ),
                            }),
                    },
                    {
                        ...(accountType === "admin" && {
                            path: "/admin-panel/:tabName",
                            element: <AdminPanel />,
                        }),
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <AdminPanel />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        ...(!redirectToAgreements &&
                            usesRightsTransfer && {
                                path: "/transferees",
                                element: <Transferees />,
                            }),
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Transferees />
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: "/transferees/:id",
                        element: <Transferees />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Transferees />
                            </ErrorBoundary>
                        ),
                    },
                ],
            },
            {
                path: "/statement/download",
                element: <DownloadStatementArchive />,
                errorElement: (
                    <ErrorBoundary translate={t}>
                        <DownloadStatementArchive />
                    </ErrorBoundary>
                ),
            },
            {
                path: "/beneficiaries/new",
                element: <BeneficiaryFormContainer mode="new" />,
                errorElement: (
                    <ErrorBoundary translate={t}>
                        <BeneficiaryFormContainer mode="new" />
                    </ErrorBoundary>
                ),
            },
            {
                path: "/beneficiars/:id/connect-title",
                element: <TermFormContainer mode="new" from="beneficiars" />,
                errorElement: (
                    <ErrorBoundary translate={t}>
                        <TermFormContainer mode="new" />
                    </ErrorBoundary>
                ),
            },
            {
                path: "/new-title",
                element: <TitleFormContainer mode="new" />,
                errorElement: (
                    <ErrorBoundary translate={t}>
                        <TitleFormContainer mode="new" />
                    </ErrorBoundary>
                ),
            },
            {
                path: "/transferees/new",
                element: <TransfereesFormContainer mode="new" />,
                errorElement: (
                    <ErrorBoundary translate={t}>
                        <TransfereesFormContainer mode="new" />
                    </ErrorBoundary>
                ),
            },
            {
                path: "/transfer-terms/new",
                element: <TransferTermFormContainer mode="new" />,
                errorElement: (
                    <ErrorBoundary translate={t}>
                        <TransferTermFormContainer mode="new" />
                    </ErrorBoundary>
                ),
            },
            {
                path: "/settings/partner-statements/new",
                element: <StatementFormContainer mode="new" type="statement" />,
                errorElement: (
                    <ErrorBoundary translate={t}>
                        <StatementFormContainer mode="new" type="statement" />
                    </ErrorBoundary>
                ),
            },
            {
                path: "/work/:id/connect-beneficiar",
                element: <TermFormContainer mode="new" from="works" />,
                errorElement: (
                    <ErrorBoundary translate={t}>
                        <TermFormContainer mode="new" />
                    </ErrorBoundary>
                ),
            },
            {
                path: "/title/:id/connect-beneficiar",
                element: <TermFormContainer mode="new" from="titles" />,
                errorElement: (
                    <ErrorBoundary translate={t}>
                        <TermFormContainer mode="new" />
                    </ErrorBoundary>
                ),
            },
            {
                path: "/callback",
                element: <OAuthCallbackComponent />,
                children: [
                    {
                        path: "/callback/:type",
                        element: <OAuthCallbackComponent />,
                        errorElement: (
                            <ErrorBoundary translate={t}>
                                <Navigate to="/callback/:type" replace />
                            </ErrorBoundary>
                        ),
                    },
                ],
            },
            // {path: "/settings/channels/new-channel", element: <NewChannelPage />, errorElement: <ErrorBoundary translate={t}><NewChannelPage/></ErrorBoundary>},
            // {path: "/settings/channels/edit/:id", element: <ChannelWrapper />, errorElement: <ErrorBoundary translate={t}><ChannelWrapper/></ErrorBoundary>},
        ]);

        return (
            <>
                <ChangelogModal
                    isOpen={showChangelog}
                    changelogMessage={lastImportantChangelog}
                    onClose={() => handleCloseChangelog()}
                />
                <ModalBox
                    onClose={() => {}}
                    open={closingPeriodModal}
                    mainTitle={t("NavigationBar.ClosingPeriodTitle")}
                    showCloseButton={false}
                >
                    <div style={{ textAlign: "center" }}>
                        <span style={{ color: "#ED6E6E", display: "block" }}>
                            {t("NavigationBar.ClosingPeriodFirstText")}
                        </span>
                        <span>{t("NavigationBar.ClosingPeriodLastText")}</span>
                    </div>
                </ModalBox>
                <PortalType.Provider value={portal}>{renderOnEveryPublisherPortal(router)}</PortalType.Provider>
            </>
        );
    }

    if (!companySettings) {
        const router = sentryCreateBrowserRouter([
            {
                path: "*",
                element: (
                    <Spinner
                        sx={{
                            position: "absolute",
                            left: "50%",
                            top: "50%",
                            transform: "translateX(-50%) translateY(-50%)",
                        }}
                    />
                ),
                errorElement: (
                    <ErrorBoundary translate={t}>
                        <Spinner />
                    </ErrorBoundary>
                ),
            },
        ]);
        return <>{renderOnEveryRoute(router)}</>;
    }

    return null;
};

export default App;
